
import React, { useState } from 'react';
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { AssessmentQuestion } from './AssessmentQuestion';

interface ProductCraftSectionProps {
  onComplete: (data: { score: number; responses: Record<string, number> }) => void;
  initialData?: { score: number; responses: Record<string, number> };
}

export const ProductCraftSection = ({ onComplete, initialData }: ProductCraftSectionProps) => {
  const { toast } = useToast();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState<Record<string, number>>(
    initialData?.responses || {}
  );

  const questions = [
    {
      id: 'productStrategy',
      title: 'Product Strategy',
      question: 'How would you describe your product strategy impact?',
      options: [
        { text: 'I successfully ship features that users love. I understand prioritization and can execute on a defined roadmap.', points: 3 },
        { text: 'I lead product turnaround initiatives for existing products. I diagnose issues and create effective solutions that revitalize products.', points: 6 },
        { text: 'I develop products that change how people behave in my market. My products have influenced competitors and established new standards.', points: 9 },
        { text: 'I transform product direction at an organizational level. My strategic vision reshapes business models and creates new possibilities for the company.', points: 12 }
      ],
      evidence: [
        { 
          title: 'Key Strategy Example',
          description: 'Share a specific example of your product strategy impact'
        },
        {
          title: 'Impact Measurement',
          description: 'How did you measure the success of your strategy?'
        }
      ]
    },
    {
      id: 'technicalPartnership',
      title: 'Technical Partnership',
      question: 'How do you work with engineering teams?',
      options: [
        { text: 'I build strong relationships with engineers and effectively translate business needs into product requirements they understand.', points: 2 },
        { text: 'I participate meaningfully in technical discussions and help make trade-off decisions. I understand the "why" behind technical constraints.', points: 4 },
        { text: 'I guide significant architectural choices by balancing business needs with technical realities. Engineering leaders seek my input on major decisions.', points: 7 },
        { text: 'I shape technical strategy that enables new product possibilities. CTOs and engineering executives treat me as a strategic partner in technical direction.', points: 10 }
      ],
      evidence: [
        {
          title: 'Technical Collaboration',
          description: 'Describe a significant technical partnership'
        },
        {
          title: 'Decision Influence',
          description: 'How did you influence technical direction?'
        }
      ]
    },
    {
      id: 'dataLeadership',
      title: 'Data-Driven Leadership',
      question: 'How do you use data in your decision making?',
      options: [
        { text: 'I track key performance indicators and use data to understand how products are performing. I can spot trends in the numbers.', points: 2 },
        { text: 'I identify meaningful patterns in data that others miss. I connect disparate data points to uncover opportunities and diagnose problems.', points: 4 },
        { text: 'I develop prediction models that forecast outcomes and future performance. I can estimate impact before launching new initiatives.', points: 6 },
        { text: 'I create analytical frameworks that the broader organization adopts. My approach to data influences how multiple teams make decisions.', points: 8 }
      ],
      evidence: [
        {
          title: 'Data Application',
          description: 'Share an example of how you used data to drive decisions'
        },
        {
          title: 'Analysis Method',
          description: 'What approach did you use to analyze the data?'
        }
      ]
    }
  ];

  const handleSelect = (points: number) => {
    const currentQuestionId = questions[currentQuestion].id;
    setResponses(prev => ({
      ...prev,
      [currentQuestionId]: points
    }));
  };

  const calculateScore = (responses: Record<string, number>): number => {
    return Object.values(responses).reduce((sum, points) => sum + points, 0);
  };

  const handleNext = () => {
    if (currentQuestion === questions.length - 1) {
      const finalData = {
        score: calculateScore(responses),
        responses
      };
      
      toast({
        title: "Section completed",
        description: "Your Product Craft & Technical Excellence assessment has been saved.",
      });
      
      onComplete(finalData);
    } else {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentQuestion(prev => Math.max(0, prev - 1));
  };

  const currentQuestionData = questions[currentQuestion];
  const selectedPoints = responses[currentQuestionData.id];

  return (
    <Card className="max-w-3xl mx-auto">
      <AssessmentQuestion
        title={currentQuestionData.title}
        question={currentQuestionData.question}
        options={currentQuestionData.options}
        evidence={currentQuestionData.evidence}
        onNext={handleNext}
        onPrevious={handlePrevious}
        onSelect={handleSelect}
        selectedPoints={selectedPoints}
        showPrevious={currentQuestion > 0}
      />
    </Card>
  );
};

export default ProductCraftSection;
